import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { readApiJson } from '../lib/api';

const LEGACY_MIGRATION_PREFIX = 'cbat_attempts_migrated_v1';

const toServerAttempt = (attempt) => ({
  client_attempt_id: String(attempt.id),
  module_name: attempt.moduleName,
  mode: attempt.mode || null,
  difficulty: attempt.difficulty || null,
  accuracy: attempt.accuracy !== null
    && attempt.accuracy !== undefined
    && Number.isFinite(Number(attempt.accuracy))
    ? Number(attempt.accuracy)
    : null,
  result: attempt,
  completed_at: attempt.timestamp || new Date().toISOString(),
  mock_run_id: attempt.mockRunId || null,
  service_profile: attempt.serviceProfile || null,
  battery_profile_id: attempt.batteryProfileId || null,
  battery_profile_version: Number.isFinite(Number(attempt.batteryProfileVersion)) ? Number(attempt.batteryProfileVersion) : null,
  diagnostic_run_id: attempt.diagnosticRunId || null,
  diagnostic_profile_id: attempt.diagnosticProfileId || null,
  diagnostic_profile_version: Number.isFinite(Number(attempt.diagnosticProfileVersion)) ? Number(attempt.diagnosticProfileVersion) : null,
  scoring_version: Number.isFinite(Number(attempt.scoringVersion)) ? Number(attempt.scoringVersion) : null
});

const fromServerAttempt = (row) => ({
  ...(row.result || {}),
  id: row.client_attempt_id,
  moduleName: row.module_name,
  mode: row.mode,
  difficulty: row.difficulty,
  ...(row.accuracy !== null ? { accuracy: Number(row.accuracy) } : {}),
  ...(row.mock_run_id ? { mockRunId: row.mock_run_id } : {}),
  ...(row.service_profile ? { serviceProfile: row.service_profile } : {}),
  ...(row.battery_profile_id ? { batteryProfileId: row.battery_profile_id } : {}),
  ...(row.battery_profile_version !== null && row.battery_profile_version !== undefined ? { batteryProfileVersion: row.battery_profile_version } : {}),
  ...(row.diagnostic_run_id ? { diagnosticRunId: row.diagnostic_run_id } : {}),
  ...(row.diagnostic_profile_id ? { diagnosticProfileId: row.diagnostic_profile_id } : {}),
  ...(row.diagnostic_profile_version !== null && row.diagnostic_profile_version !== undefined ? { diagnosticProfileVersion: row.diagnostic_profile_version } : {}),
  ...(row.scoring_version !== null && row.scoring_version !== undefined ? { scoringVersion: row.scoring_version } : {}),
  timestamp: row.completed_at
});

const migrationKey = (userId) => `${LEGACY_MIGRATION_PREFIX}:${userId}`;

const hasAuthenticatedSession = async () => {
  if (!isSupabaseConfigured || !supabase) return false;
  const { data } = await supabase.auth.getSession();
  return Boolean(data?.session?.user);
};

const toUploadBatches = (attempts) => {
  const batches = [];
  let batch = [];
  let encodedBytes = 0;
  attempts.forEach((attempt) => {
    const row = toServerAttempt(attempt);
    const rowBytes = new Blob([JSON.stringify(row)]).size;
    if (batch.length && (batch.length >= 100 || encodedBytes + rowBytes > 750000)) {
      batches.push(batch);
      batch = [];
      encodedBytes = 0;
    }
    batch.push(row);
    encodedBytes += rowBytes;
  });
  if (batch.length) batches.push(batch);
  return batches;
};

const migrateLegacySupabaseAttempts = async (userId) => {
  if (!isSupabaseConfigured || !supabase || !userId || localStorage.getItem(migrationKey(userId))) {
    return [];
  }
  const data = await readApiJson('/account/attempts/migrate-legacy', { method: 'POST' });
  const attempts = (data?.attempts || []).map(fromServerAttempt);
  localStorage.setItem(migrationKey(userId), 'true');
  return attempts;
};

export const syncAttempt = async (attempt) => {
  try {
    if (!await hasAuthenticatedSession()) return { skipped: true, error: null };
    await readApiJson('/account/attempts', {
      method: 'POST',
      body: JSON.stringify(toServerAttempt(attempt))
    });
    return { error: null };
  } catch (error) {
    return { error };
  }
};

// The server saves every valid run in a batch and lists the ones it refused
// (`rejected`, with why), so one bad run never holds back the rest.
export const syncAttempts = async (attempts, userId) => {
  if (!userId || attempts.length === 0) return { error: null, rejected: [] };
  const rejected = [];
  try {
    const batches = toUploadBatches(attempts);
    for (const batch of batches) {
      const data = await readApiJson('/account/attempts/batch', {
        method: 'POST',
        body: JSON.stringify({ attempts: batch })
      });
      rejected.push(...(data?.rejected || []));
    }
    return { error: null, rejected };
  } catch (error) {
    return { error, rejected };
  }
};

export const fetchAttempts = async (userId) => {
  if (!userId) return { attempts: [], error: null };
  try {
    const data = await readApiJson('/account/attempts');
    let attempts = (data?.attempts || []).map(fromServerAttempt);
    const migrated = await migrateLegacySupabaseAttempts(userId);
    if (migrated.length) {
      const byId = new Map(attempts.map((attempt) => [String(attempt.id), attempt]));
      migrated.forEach((attempt) => byId.set(String(attempt.id), attempt));
      attempts = Array.from(byId.values()).sort((left, right) => (
        new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()
      ));
    }
    return { attempts, error: null };
  } catch (error) {
    return { attempts: [], error };
  }
};

export const deleteAttempts = async (moduleName = null) => {
  try {
    if (!await hasAuthenticatedSession()) return { skipped: true, error: null };
    const query = moduleName ? `?module_name=${encodeURIComponent(moduleName)}` : '';
    await readApiJson(`/account/attempts${query}`, { method: 'DELETE' });
    return { error: null };
  } catch (error) {
    return { error };
  }
};
